import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { addWeatherLocation } from '../../features/weather/weatherSlice';
import Card from '../ui/Card';
import Button from '../ui/Button';
import { MapPin, Search } from 'react-feather';
import LoadingSpinner from '../ui/LoadingSpinner';

interface Props {
  onSuccess: () => void;
}

const WeatherLocationForm: React.FC<Props> = ({ onSuccess }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [location, setLocation] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!location.trim()) {
      setError(t('weather.validation.locationRequired'));
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await dispatch(addWeatherLocation(location)).unwrap();
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('weather.validation.locationInvalid'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="bg-white">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
            {t('weather.locationName')}
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
              <MapPin className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              id="location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder={t('weather.selectLocation')}
              disabled={isSubmitting}
              className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
            />
            {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
          </div>
          <p className="mt-2 text-xs text-gray-500">
            Enter a city name, region, or country (e.g., "Mumbai", "Delhi", "Karnataka")
          </p>
        </div>

        <div className="flex justify-end space-x-3">
          <Button
            type="button"
            variant="secondary"
            onClick={onSuccess}
            disabled={isSubmitting}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            disabled={!location.trim() || isSubmitting}
            className="flex items-center gap-2"
          >
            {isSubmitting ? (
              <LoadingSpinner size="sm" color="white" />
            ) : (
              <>
                <Search size={16} />
                {t('common.add')}
              </>
            )}
          </Button>
        </div>
      </form>
    </Card>
  );
};

export default WeatherLocationForm;